/* ONE BOUT — <ob-faq> (sections/faq.liquid, Sprint 4 / F).
   Tabs (WAI-ARIA tabs pattern: click, ←/→/Home/End) toggle server-rendered panels, no reload; the first tab is
   active on load, or the one whose id / category matches the URL hash (#ObFaqPanel-<block id>).
   Accordions are Prestige <accordion-disclosure> (theme.js); with the "single-open" attribute, opening one closes
   the other open answers of the same panel.
   Theme editor: selecting a category or question block switches to its tab. */
class ObFaq extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = this.tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls')));

    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.select(i));
      tab.addEventListener('keydown', (event) => this.onKey(event, i));
    });

    // one answer open at a time: runs after Prestige's own summary click handler (bubbling)
    this.addEventListener('click', (event) => {
      if (!this.hasAttribute('single-open')) return;
      const summary = event.target.closest('summary');
      const item = summary && summary.closest('accordion-disclosure');
      if (!item || !this.contains(item)) return;
      requestAnimationFrame(() => {
        if (item.querySelector('details')?.getAttribute('aria-expanded') !== 'true') return;
        const scope = item.closest('[role="tabpanel"]') || this;
        scope.querySelectorAll('accordion-disclosure').forEach((other) => {
          if (other !== item && other.querySelector('details')?.getAttribute('aria-expanded') === 'true') other.close?.();
        });
      });
    });

    this.addEventListener('shopify:block:select', (event) => {
      const tabIndex = this.tabs.indexOf(event.target);
      if (tabIndex > -1) return this.select(tabIndex);
      const panel = event.target.closest('[role="tabpanel"]');
      const panelIndex = this.panels.indexOf(panel);
      if (panelIndex > -1) this.select(panelIndex);
    });

    const hashIndex = this.panels.findIndex((panel) => panel && `#${panel.id}` === window.location.hash);
    if (hashIndex > -1) this.select(hashIndex);
  }

  onKey(event, i) {
    const last = this.tabs.length - 1;
    const next = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: last }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    this.select(next > last ? 0 : next < 0 ? last : next, true);
  }

  select(index, focus = false) {
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      if (active) {
        if (focus) tab.focus();
        // keep the active tab in view inside the horizontally scrolling tab row (mobile)
        const row = tab.parentElement;
        if (row && row.scrollWidth > row.clientWidth) {
          const left = tab.offsetLeft - (row.clientWidth - tab.offsetWidth) / 2;
          row.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
        }
      }
    });
    this.panels.forEach((panel, i) => {
      if (panel) panel.hidden = i !== index;
    });
  }
}

if (!customElements.get('ob-faq')) {
  customElements.define('ob-faq', ObFaq);
}
